import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { PersonWorkingService } from '../../services/person-working.service';
import { IListBox } from 'src/app/shared/interfaces/IClaveValor';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { MaterialModule } from 'src/app/material.module';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { catchError, finalize, forkJoin, of, throwError } from 'rxjs';
import { MiAdmPersonWorking } from '../../interfaces/IAdmPersonWorking';
import { TextareaModule } from 'primeng/textarea';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-modal-info-laboral',
  imports: [ReactiveFormsModule, InputTextModule, StackDirectives, Select, MaterialModule, TextareaModule, CommonModule],
  templateUrl: './modal-info-laboral.html',
  styleUrl: './modal-info-laboral.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalInfoLaboral {
  constructor(
    private dialogRef: MatDialogRef<ModalInfoLaboral>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      idEncrypt: string;
      codEmp: string;
      tipIde: string;
      workingData: MiAdmPersonWorking;
    }
  ) {}

  private loader = inject(LoaderService);
  private toastr = inject(ToastrService);
  private personWorkingService = inject(PersonWorkingService);
  private ac = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  yearsList: IListBox[];
  monthsList: IListBox[];
  isEdit: boolean = false;

  form = this.fb.group({
    mesIni: ['', [Validators.required]],
    anoIni: ['', [Validators.required]],
    mesFin: ['', [Validators.required]],
    anoFin: ['', [Validators.required]],
    actLab: [false, [Validators.required]],
    empresa: ['', [Validators.required]],
    cargo: ['', [Validators.required, Validators.maxLength(100)]],
    funciones: ['', [Validators.required]]
  });

  ngOnInit() {
    this.loadData();
    this.cdr.markForCheck();
    this.form.markAllAsTouched();

    this.form.get('actLab')?.valueChanges.subscribe((isWorking) => {
      if (isWorking) {
        // Si trabaja actualmente, quitar validadores y limpiar valores
        this.form.get('mesFin')?.clearValidators();
        this.form.get('anoFin')?.clearValidators();
        this.form.get('mesFin')?.setValue('');
        this.form.get('anoFin')?.setValue('');
      } else {
        // Si NO trabaja → agregar validadores
        this.form.get('mesFin')?.setValidators(Validators.required);
        this.form.get('anoFin')?.setValidators(Validators.required);
      }
      this.form.get('mesFin')?.updateValueAndValidity();
      this.form.get('anoFin')?.updateValueAndValidity();
    });
  }

  getErrorMessage(controlName: string, errorMessage: string): string | null {
    const control = this.form.get(controlName);
    if (control?.touched) {
      if (control.hasError('required')) {
        return errorMessage ?? 'Este campo es requerido';
      }
      if (control.hasError('pattern')) {
        return 'Solo se permiten números';
      }
      if (control.hasError('minlength')) {
        const minLength = control.getError('minlength').requiredLength;
        return `Mínimo ${minLength} caracteres`;
      }
      if (control.hasError('email')) {
        return 'Correo electrónico inválido';
      }
    }
    return null;
  }

  closeDialog(success: boolean) {
    this.dialogRef.close(success);
  }

  loadData() {
    this.loader.show('Cargando datos...');
    forkJoin({
      years: of(this.generateYearsList()),
      months: of(this.generateMonthsList())
    })
      .pipe(finalize(() => this.loader.hide()))
      .subscribe({
        next: ({ years, months }) => {
          this.yearsList = years;
          this.monthsList = months;

          // Si hay datos laborales, llenar el formulario
          if (this.data.workingData) {
            this.isEdit = true;
            this.form.patchValue({
              mesIni: this.data.workingData.mesInicio.toString(),
              anoIni: this.data.workingData.anioInicio.toString(),
              mesFin: this.data.workingData.mesFin ? this.data.workingData.mesFin.toString() : '',
              anoFin: this.data.workingData.anioFin ? this.data.workingData.anioFin.toString() : '',
              actLab: this.data.workingData.actualmenteLaborando,
              empresa: this.data.workingData.empresa,
              cargo: this.data.workingData.cargo,
              funciones: this.data.workingData.funciones
            });
          }
          this.loader.hide();
        },
        error: (e) => {
          // console.log('Error', e);
        }
      });
  }

  generateMonthsList(): IListBox[] {
    const months = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre'
    ];

    return months.map((month, index) => ({
      label: month,
      id: (index + 1).toString()
    }));
  }

  generateYearsList(): IListBox[] {
    const currentYear = new Date().getFullYear();
    const years: IListBox[] = [];

    for (let i = 0; i <= 50; i++) {
      const year = currentYear - i;
      years.push({
        label: year.toString(),
        id: year.toString()
      });
    }

    return years;
  }

  onCreate() {
    if (this.form.valid) {
      this.loader.show('Guardando información laboral...');
      const payload = this.setPersonWorkingData();
      // console.log('Payload a enviar:', payload);
      this.personWorkingService.createFull(this.data.idEncrypt, payload).subscribe({
        next: (response) => {
          this.loader.hide();
          this.toastr.success('Información laboral guardada con éxito');
          this.closeDialog(true);
        },
        error: (err) => {
          this.loader.hide();
          this.toastr.error('Error al guardar la información laboral');
        }
      });
    } else {
      this.form.markAllAsTouched(); // Para mostrar errores
    }
  }

  onEdit() {
    if (this.form.valid) {
      this.loader.show('Actualizando información laboral...');
      const payload = this.setPersonWorkingData();
      console.log('Payload a enviar:', payload);
      this.personWorkingService.updateFull(this.data.idEncrypt, payload).subscribe({
        next: (response) => {
          this.loader.hide();
          this.toastr.success('Información laboral actualizada con éxito');
          this.closeDialog(true);
        },
        error: (err) => {
          console.error('Error al actualizar la información laboral:', err);
        }
      });
    } else {
      this.form.markAllAsTouched(); // Para mostrar errores
    }
  }

  setPersonWorkingData(): MiAdmPersonWorking {
    const isWorking = this.form.value.actLab;
    return {
      id: this.isEdit ? this.data.workingData.id : 0,
      idTipoIdentificacion: Number.parseInt(this.data.tipIde),
      identificacion: this.data.codEmp,
      mesInicio: Number.parseInt(this.form.value.mesIni!),
      anioInicio: Number.parseInt(this.form.value.anoIni!),
      mesFin: isWorking ? null : Number.parseInt(this.form.value.mesFin!),
      anioFin: isWorking ? null : Number.parseInt(this.form.value.anoFin!),
      actualmenteLaborando: this.form.value.actLab!,
      empresa: this.form.value.empresa!,
      cargo: this.form.value.cargo!,
      funciones: this.form.value.funciones!,
      idEstado: 1
    };
  }

  sinExperiencia() {
    this.form.patchValue({
      empresa: 'Sin experiencia',
      cargo: 'Sin experiencia',
      funciones: 'Sin experiencia',
      mesIni: new Date().getMonth().toString(),
      anoIni: new Date().getFullYear().toString(),
      mesFin: new Date().getMonth().toString(),
      anoFin: new Date().getFullYear().toString(),
      actLab: false
    });
    this.onCreate();
  }
}
